<script setup>
import { computed } from 'vue';

import { HrChip } from '../primitives';

/**
 * Assist chip — a compact action chip for contextual shortcuts alongside primary content.
 *
 * @status recommended
 * @group chips
 * @useWhen Offering quick contextual actions near the content they relate to
 * @avoidWhen A toggle state is needed — use HrFilterChip or HrStatusActionChip
 * @related HrSuggestionChip
 * @related HrFilterChip
 * @event click Fired when the chip is activated.
 */
defineOptions({ name: 'HrAssistChip' });

const props = defineProps({
  /**
   * Chip label text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Add tag
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown before the label.
   * @example add
   */
  icon: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Chip size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Renders a skeleton placeholder instead of the chip. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar props applied in legacy (non-theme) mode, merged over this chip's own defaults.
   * Ignored when a design system theme is active.
   * @deprecated Only for call sites whose legacy appearance must be preserved during migration.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

// @todo Remove legacy-props when legacy theme is removed
const effectiveLegacyProps = computed(() => ({
  outline: true,
  ...props.legacyProps,
}));

</script>

<template>
  <hr-chip
    variant="assist"
    clickable
    :label="label"
    :icon="icon"
    :size="size"
    :disable="disable"
    :pending="pending"
    :legacy-props="effectiveLegacyProps"
    v-bind="$attrs"
  >
    <!-- @slot default Custom chip label content; use instead of the `label` prop for rich content. -->
    <slot />
  </hr-chip>
</template>
